import { DateTime } from "luxon";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useHospitalData } from "../../../contexts/HospitalDataProvider";
import { isNurseActiveInDepartment } from "../../../utils/formatters";
import { type Nurse, type NurseInShift, Preferences } from "../../../utils/models";
import { isAgencyNurse } from "../../../utils/warningDialogs/WarningDialog";
import { HiringShiftType, isDayShift, matchesRoleGroup, ROLE_GROUPS, ROLE_HPPD_ALLOCATION, type RoleGroup, UNDERUTILIZED_ALLOCATION } from "../types";

const WEEKS_FOR_DATES = 2;
export const FIRST_WEEK_DAYS = 7;
const DAY_SHIFTS_PER_NIGHT = 2;
const HOURS_PER_SHIFT = 12;

export interface AvailableDateEntry {
	date: DateTime;
	shiftTypes: HiringShiftType[];
}

export interface UnderutilizedNurse {
	nurse: Nurse;
	scheduledShifts: number;
	allowedShifts: number;
	availableDates: AvailableDateEntry[];
	shiftCategory: HiringShiftType;
}

type UnderutilizedCandidate = Omit<UnderutilizedNurse, "shiftCategory">;

export interface SimulationEntry {
	date: DateTime;
	role: string;
	shiftType: HiringShiftType;
	nurseName: string;
	isNewHire: boolean;
}

type RoleShiftCounts = Record<HiringShiftType, number>;
type CountsByRoleShift = Record<string, RoleShiftCounts>;

/** Split a number of missing shifts into overtime, pickup, and agency portions. */
export const getMissingShiftSplit = (recurring: number): { overtime: number; pickup: number; agency: number } => {
	if (recurring <= 0) {
		return { overtime: 0, pickup: 0, agency: 0 };
	}
	const overtime = Math.max(0, Math.floor(recurring * 0.35));
	const pickup = Math.max(0, Math.floor(recurring * 0.5));
	const agency = Math.max(0, Math.floor(recurring - overtime - pickup));
	return { overtime, pickup, agency };
};

/** Picks Day or Night based on allocation caps; prefers the shift type with larger gap when both have room. */
function assignShiftCategory(counts: RoleShiftCounts, alloc: RoleShiftCounts, dayGap: number, nightGap: number): HiringShiftType | null {
	const tryDayFirst = dayGap >= nightGap;
	const [first, second] = tryDayFirst ? [HiringShiftType.Day, HiringShiftType.Night] : [HiringShiftType.Night, HiringShiftType.Day];
	if (counts[first] < alloc[first]) return first;
	if (counts[second] < alloc[second]) return second;
	return null;
}

/** Stats for a nurse over a date range: scheduled shift count and set of dates they work. */
const getNurseScheduledStats = (
	nurse: Nurse,
	dates: DateTime[],
	getShiftsForDate: (date: DateTime) => NurseInShift[],
): { scheduledShifts: number; scheduledDates: Set<string> } => {
	let scheduledShifts = 0;
	const scheduledDates = new Set<string>();
	dates.forEach((date) => {
		const shifts = getShiftsForDate(date);
		const nurseShifts = shifts.filter((s) => s.nurseId === nurse.id);
		scheduledShifts += nurseShifts.length;
		if (nurseShifts.length > 0) {
			scheduledDates.add(date.toISODate() || "");
		}
	});
	return { scheduledShifts, scheduledDates };
};

/** Dates the nurse can work (not already scheduled) and which shift types they can do per date, respecting preferences. */
const getAvailableDatesForNurse = (nurse: Nurse, dates: DateTime[], scheduledDates: Set<string>): AvailableDateEntry[] => {
	const availableDates: AvailableDateEntry[] = [];
	dates.forEach((date) => {
		const dateStr = date.toISODate() || "";
		if (scheduledDates.has(dateStr)) return;

		const dayName = date.toFormat("cccc").toLowerCase();
		const avoidPrefs = nurse.employeeWeeklyPreferences?.filter((p) => p.day.toLowerCase() === dayName && (p.type === Preferences.Avoid || p.unavailable));

		const shiftTypes: HiringShiftType[] = [];
		const avoidDay = avoidPrefs?.some((p) => {
			const s = p.shift?.toLowerCase();
			return s === HiringShiftType.Day || s === "morning" || !p.shift;
		});
		const avoidNight = avoidPrefs?.some((p) => {
			const s = p.shift?.toLowerCase();
			return s === HiringShiftType.Night || !p.shift;
		});
		if (!avoidDay) shiftTypes.push(HiringShiftType.Day);
		if (!avoidNight) shiftTypes.push(HiringShiftType.Night);

		if (shiftTypes.length > 0) {
			availableDates.push({ date, shiftTypes });
		}
	});
	return availableDates;
};

type UseHiringDataOptions = {
	includeAIRelated?: boolean;
};

export const useHiringData = (options?: UseHiringDataOptions) => {
	const { hospitalData, chosenDepartment } = useHospitalData();
	const includeAIRelated = options?.includeAIRelated ?? true;
	const [census, setCensus] = useState<number>(0);
	const [targetHppd, setTargetHppd] = useState<number>(chosenDepartment?.target_ppn ?? 0);

	const initialCensus = useMemo(() => {
		if (hospitalData.weeks.length === 0) return 0;
		const today = DateTime.now().startOf("day");
		for (const week of hospitalData.weeks) {
			for (const shift of week.weeklyShifts) {
				for (const day of shift.days) {
					if (day.date.hasSame(today, "day")) {
						return day.census_low ?? 0;
					}
				}
			}
		}
		return 0;
	}, [hospitalData.weeks]);

	useEffect(() => {
		setCensus(initialCensus);
	}, [initialCensus]);

	const dates = useMemo(() => {
		if (hospitalData.weeks.length === 0) return [];
		return hospitalData.weeks.slice(0, WEEKS_FOR_DATES).flatMap((week) => week.weeklyShifts[0]?.days?.map((d) => d.date) ?? []);
	}, [hospitalData.weeks]);

	const getShiftsForDate = useCallback(
		(date: DateTime) =>
			hospitalData.weeks.flatMap((week) =>
				week.weeklyShifts.flatMap((ws) => {
					const day = ws.days.find((d) => d.date.hasSame(date, "day"));
					return day ? day.nurses : [];
				}),
			),
		[hospitalData.weeks],
	);

	const isOvertimeOrAgency = useCallback(
		(shift: NurseInShift) => {
			const nurse = hospitalData.nurses.find((n) => n.id === shift.nurseId);
			return !!(nurse?.agencyName || shift.bonus > 0);
		},
		[hospitalData.nurses],
	);

	const getRegularShiftsForDate = useCallback(
		(date: DateTime) => {
			return getShiftsForDate(date).filter((s) => !isOvertimeOrAgency(s));
		},
		[getShiftsForDate, isOvertimeOrAgency],
	);

	const calculateNeeded = useCallback(
		(role: string, customCensus?: number) => {
			const usedCensus = customCensus ?? census;
			const allocation = ROLE_HPPD_ALLOCATION[role as RoleGroup] ?? 0;

			const totalNeededHours = usedCensus * targetHppd * allocation;

			const totalRatioUnits = DAY_SHIFTS_PER_NIGHT + 1; // e.g. 2 day : 1 night
			const dayHours = (totalNeededHours * DAY_SHIFTS_PER_NIGHT) / totalRatioUnits;
			const nightHours = totalNeededHours / totalRatioUnits;

			const day = Math.round(dayHours / HOURS_PER_SHIFT);
			const night = Math.round(nightHours / HOURS_PER_SHIFT);

			return { day, night, total: day + night };
		},
		[census, targetHppd],
	);

	/** Actual day/night shift counts for a date and role (regular shifts only). */
	const getActualsForDateRole = useCallback(
		(date: DateTime, role: string): { actualDay: number; actualNight: number } => {
			const shifts = getRegularShiftsForDate(date).filter((s) => matchesRoleGroup(s.role, role));
			const actualDay = shifts.filter((s) => isDayShift(s.shiftType)).length;
			const actualNight = shifts.filter((s) => !isDayShift(s.shiftType)).length;
			return { actualDay, actualNight };
		},
		[getRegularShiftsForDate],
	);

	/** Gap (needed - actual) for a specific date, role, and shift type. Uses editable census and target HPPD. */
	const getGapForDateRoleShift = useCallback(
		(date: DateTime, role: string, shiftType: HiringShiftType): number => {
			const needed = calculateNeeded(role);
			const { actualDay, actualNight } = getActualsForDateRole(date, role);
			const neededCount = shiftType === HiringShiftType.Day ? needed.day : needed.night;
			const actualCount = shiftType === HiringShiftType.Day ? actualDay : actualNight;
			return Math.max(0, neededCount - actualCount);
		},
		[calculateNeeded, getActualsForDateRole],
	);

	// Missing shifts: OT and agency shifts don't count as "actual"
	const missingShifts = useMemo(() => {
		let totalMissing = 0;
		const byRole: Record<string, number> = {};
		for (const role of ROLE_GROUPS) {
			byRole[role] = 0;
		}
		for (const date of dates.slice(0, FIRST_WEEK_DAYS)) {
			const regularShifts = getRegularShiftsForDate(date);
			for (const role of ROLE_GROUPS) {
				const needed = calculateNeeded(role).total;
				const actual = regularShifts.filter((s) => matchesRoleGroup(s.role, role)).length;
				const missing = needed - actual;
				if (missing !== 0) {
					totalMissing += missing;
					byRole[role] += missing;
				}
			}
		}
		return { total: totalMissing, byRole };
	}, [dates, getRegularShiftsForDate, calculateNeeded]);

	const shiftStats = useMemo(() => {
		let totalShifts = 0;
		let totalHours = 0;
		const byRole: Record<string, { recurring: number; pickup: number; overtime: number; agency: number }> = {};
		for (const role of ROLE_GROUPS) {
			byRole[role] = { recurring: 0, pickup: 0, overtime: 0, agency: 0 };
		}
		let finalTotal = 0;

		if (hospitalData.weeks.length > 0) {
			const week = hospitalData.weeks[0];
			for (const ws of week.weeklyShifts) {
				for (const day of ws.days) {
					for (const shift of day.nurses) {
						let duration = shift.endTime.diff(shift.startTime, "hours").hours;
						if (duration < 0) duration += 24;
						totalHours += duration;

						const matchedRole = ROLE_GROUPS.find((group) => matchesRoleGroup(shift.role, group));
						if (!matchedRole) continue;
						totalShifts++;
						const stats = byRole[matchedRole];
						const nurse = hospitalData.nurses.find((n) => n.id === shift.nurseId);
						const isAgency = !!(nurse?.agencyName || (nurse && isAgencyNurse(nurse.employmentType)));
						if (isAgency) stats.agency++;
						else if (shift.requestId) stats.pickup++;
						else if (shift.bonus > 0) stats.overtime++;
						else stats.recurring++;
					}
				}
			}

			// Always redistribute for display variety - order: recurring > overtime > pickup > agency
			for (const role of ROLE_GROUPS) {
				const r = byRole[role];
				// Target total = census * 3.5 * allocation for the role * 7 days
				const allocation = ROLE_HPPD_ALLOCATION[role as RoleGroup] ?? 0;
				const total = (initialCensus / 3.5) * allocation * FIRST_WEEK_DAYS;

				if (total > 0) {
					const { overtime, pickup, agency } = getMissingShiftSplit(r.recurring);
					r.overtime = overtime;
					r.pickup = pickup;
					r.agency = agency;
					finalTotal += r.recurring + r.overtime + r.pickup + r.agency;
				}
			}
		}

		return { byRole, totalShifts: finalTotal, totalHours };
	}, [hospitalData.weeks, hospitalData.nurses, initialCensus]);

	const predictedHppd = useMemo(() => {
		if (census === 0) return 0.0;
		return shiftStats.totalHours / (census * FIRST_WEEK_DAYS);
	}, [shiftStats.totalHours, census]);

	const underutilizedCandidates = useMemo((): UnderutilizedCandidate[] => {
		if (!includeAIRelated || dates.length === 0) return [];

		const departmentNurses = hospitalData.nurses.filter(
			(n) =>
				chosenDepartment &&
				n.departments.includes(chosenDepartment.department_id) &&
				isNurseActiveInDepartment(n, chosenDepartment.department_id) &&
				!n.agencyName,
		);
		return departmentNurses
			.map((nurse) => {
				const { scheduledShifts, scheduledDates } = getNurseScheduledStats(nurse, dates, getShiftsForDate);
				const allowedShifts = nurse.allowedShifts ?? 0;
				const twoWeekCapacity = allowedShifts * WEEKS_FOR_DATES;
				if (scheduledShifts === 0 || scheduledShifts >= twoWeekCapacity || allowedShifts === 0) return null;
				const availableDates = getAvailableDatesForNurse(nurse, dates, scheduledDates);
				if (availableDates.length === 0) return null;
				return { nurse, scheduledShifts, allowedShifts, availableDates };
			})
			.filter((n): n is UnderutilizedCandidate => n !== null)
			.sort((a, b) => b.scheduledShifts - a.scheduledShifts);
	}, [includeAIRelated, dates, hospitalData.nurses, chosenDepartment, getShiftsForDate]);

	/** Total missing shift slots per role across all dates (day + night). Uses editable census and target HPPD. */
	const aiRecommendation = useMemo(() => {
		if (!includeAIRelated || dates.length === 0) return ROLE_GROUPS.map((role) => ({ role, day: 0, night: 0, total: 0 }));

		return ROLE_GROUPS.map((role) => {
			const needed = calculateNeeded(role);
			let totalDayGap = 0;
			let totalNightGap = 0;
			for (const date of dates) {
				const { actualDay, actualNight } = getActualsForDateRole(date, role);
				const dayGap = Math.max(0, needed.day - actualDay);
				const nightGap = Math.max(0, needed.night - actualNight);
				totalDayGap += dayGap;
				totalNightGap += nightGap;
			}
			const total = totalDayGap + totalNightGap;
			return { role, day: totalDayGap, night: totalNightGap, total };
		});
	}, [includeAIRelated, dates, calculateNeeded, getActualsForDateRole]);

	const underutilizedNurses = useMemo((): UnderutilizedNurse[] => {
		if (!includeAIRelated) return [];

		const result: UnderutilizedNurse[] = [];
		const countByRoleShift: CountsByRoleShift = {};

		for (const u of underutilizedCandidates) {
			const roleGroup = ROLE_GROUPS.find((group) => matchesRoleGroup(u.nurse.role, group)) as RoleGroup | undefined;
			if (!roleGroup) continue;

			if (!countByRoleShift[roleGroup]) {
				countByRoleShift[roleGroup] = { [HiringShiftType.Day]: 0, [HiringShiftType.Night]: 0 };
			}
			const counts = countByRoleShift[roleGroup];
			const alloc = UNDERUTILIZED_ALLOCATION[roleGroup];
			const rec = aiRecommendation.find((r) => r.role === roleGroup);
			const dayGap = rec?.day ?? 0;
			const nightGap = rec?.night ?? 0;

			const shiftCategory = assignShiftCategory(counts, alloc, dayGap, nightGap);
			if (shiftCategory === null) continue;

			counts[shiftCategory]++;
			const availableDatesSorted = [...u.availableDates].sort(
				(a, b) => getGapForDateRoleShift(b.date, roleGroup, shiftCategory) - getGapForDateRoleShift(a.date, roleGroup, shiftCategory),
			);
			result.push({ ...u, shiftCategory, availableDates: availableDatesSorted });
		}

		return result;
	}, [includeAIRelated, underutilizedCandidates, aiRecommendation, getGapForDateRoleShift]);

	return {
		census,
		setCensus,
		targetHppd,
		setTargetHppd,
		dates,
		getShiftsForDate,
		shiftStats,
		predictedHppd,
		calculateNeeded,
		missingShifts,
		hospitalData,
		aiRecommendation,
		underutilizedNurses,
	};
};
